import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Modal, Btn, Input, Textarea, Select, Card, Badge } from "@/components/UI";
import { FileText, Upload, Download, Trash2, Plus, X, RefreshCw, FlaskConical, Sparkles, Globe, Check, CheckCircle, AlertTriangle, Package } from "lucide-react";

/* Iter241 — Trade names (alias) manager. Aynı canonical materyalin farklı
   tedarikçi/marka altında geçtiği ticari isimleri buradan yönetiyoruz.
   Materyal ASLA duplicate açılmaz. */
function TradeNamesSection({ materialId, card, onChanged }) {
  const [items, setItems] = React.useState([]);
  const [form, setForm] = React.useState({ supplier: "", manufacturer: "", trade_name: "", grade: "", notes: "" });
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setItems(card.trade_names || []); }, [card]);
  const add = async () => {
    if (!form.trade_name.trim()) return alert("Ticari ad zorunlu");
    setBusy(true);
    try {
      const r = await api.post(`/raw-material-cards/${materialId}/trade-names`, form);
      if (r.duplicate) alert("Aynı kombinasyon zaten kayıtlı.");
      setForm({ supplier: "", manufacturer: "", trade_name: "", grade: "", notes: "" });
      onChanged?.();
    } catch (e) { alert(e.response?.data?.detail || "Eklenemedi"); }
    finally { setBusy(false); }
  };
  const del = async (aid) => {
    if (!window.confirm("Bu ticari isim silinsin mi?")) return;
    try { await api.del(`/raw-material-cards/${materialId}/trade-names/${aid}`); onChanged?.(); }
    catch (e) { alert(e.response?.data?.detail || "Silinemedi"); }
  };
  return (
    <div className="border rounded-lg p-3 bg-indigo-50/30 border-indigo-200" data-testid="rmc-trade-names-section">
      <div className="flex items-center gap-2 text-xs font-semibold text-indigo-700 mb-2">
        <Package size={13}/> Alternatif Ticari İsimler ({items.length})
        <span className="font-normal text-slate-500">— aynı canonical malzeme, farklı tedarikçi/marka için</span>
      </div>
      {items.length > 0 && (
        <table className="w-full text-xs mb-2">
          <thead className="text-slate-500">
            <tr><th className="text-left p-1">Tedarikçi</th><th className="text-left p-1">Üretici</th><th className="text-left p-1">Ticari Ad</th><th className="text-left p-1">Grade</th><th></th></tr>
          </thead>
          <tbody>
            {items.map(a => (
              <tr key={a.alias_id} className="border-t" data-testid={`rmc-trade-name-${a.alias_id}`}>
                <td className="p-1">{a.supplier || "—"}</td>
                <td className="p-1">{a.manufacturer || "—"}</td>
                <td className="p-1 font-semibold">{a.trade_name}</td>
                <td className="p-1">{a.grade || "—"}</td>
                <td className="p-1"><button onClick={() => del(a.alias_id)} className="text-red-600 hover:bg-red-50 p-1 rounded" data-testid={`rmc-trade-del-${a.alias_id}`}><Trash2 size={10}/></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="grid grid-cols-5 gap-2 text-xs">
        <input placeholder="Tedarikçi" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} className="border rounded px-2 py-1" data-testid="rmc-trade-add-supplier"/>
        <input placeholder="Üretici" value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })} className="border rounded px-2 py-1" data-testid="rmc-trade-add-manufacturer"/>
        <input placeholder="Ticari Ad *" value={form.trade_name} onChange={e => setForm({ ...form, trade_name: e.target.value })} className="border rounded px-2 py-1 font-semibold" data-testid="rmc-trade-add-name"/>
        <input placeholder="Grade" value={form.grade} onChange={e => setForm({ ...form, grade: e.target.value })} className="border rounded px-2 py-1" data-testid="rmc-trade-add-grade"/>
        <button onClick={add} disabled={busy} data-testid="rmc-trade-add-btn"
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded px-2 py-1 font-semibold disabled:opacity-50">+ Ekle</button>
      </div>
    </div>
  );
}


const DOC_TYPES = [
  ["SDS", "SDS"], ["TDS", "TDS"], ["SPECIFICATION", "Specification"],
  ["IFRA", "IFRA"], ["ALLERGEN", "Allergen Declaration"],
  ["COA", "COA"], ["SAMPLE_COA", "Sample COA"],
  ["CERTIFICATE", "Certificate"], ["OTHER", "Other"],
];

/* iter321 — Belge Eşleştirme Aliasları (doc_aliases).
   Trade Name DEĞİL. Yalnız dosya adı eşleştirmesi için kullanılan,
   kullanıcı tarafından doğrulanmış alternatif isimler. Admin OR
   System Authority ekleyebilir/silebilir; normal user sadece görür. */
function DocAliasesSection({ materialId, onChanged }) {
  const [data, setData] = React.useState({ aliases: [], can_manage: false });
  const [newName, setNewName] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(async () => {
    try {
      const r = await api.get(`/raw-material-cards/${materialId}/doc-aliases`);
      setData(r);
    } catch (e) { /* ignore */ }
  }, [materialId]);
  React.useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post(`/raw-material-cards/${materialId}/doc-aliases`,
                      { name: newName.trim() });
      setNewName("");
      await load();
      onChanged?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Alias eklenemedi");
    } finally { setBusy(false); }
  };
  const del = async (name) => {
    if (!window.confirm(`"${name}" alias'ı silinsin mi? Geçmiş belge bağlantıları etkilenmez.`)) return;
    try {
      await api.del(`/raw-material-cards/${materialId}/doc-aliases/${encodeURIComponent(name)}`);
      await load();
      onChanged?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Silinemedi");
    }
  };
  return (
    <div className="border rounded-lg p-3 bg-fuchsia-50/30 border-fuchsia-200" data-testid="rmc-doc-aliases-section">
      <div className="flex items-center gap-2 text-xs font-semibold text-fuchsia-700 mb-2">
        <FileText size={13}/> Belge Eşleştirme Aliasları ({(data.aliases || []).length})
        <span className="font-normal text-slate-500">— yalnız dosya adı eşleştirme amaçlı · Ticari ad değildir</span>
      </div>
      {(data.aliases || []).length > 0 && (
        <ul className="space-y-1 mb-2">
          {data.aliases.map((a, i) => (
            <li key={i} className="flex items-center gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1" data-testid={`rmc-alias-row-${i}`}>
              <span className="flex-1 font-medium">{a.name}</span>
              <span className="text-[10px] text-slate-400 font-mono" title={`${a.verified_by || "?"} · ${a.verified_at || ""}`}>
                {a.verified_by || "?"}
              </span>
              {data.can_manage && (
                <button onClick={() => del(a.name)}
                        className="text-red-600 hover:bg-red-50 p-0.5 rounded"
                        data-testid={`rmc-alias-del-${i}`}
                        title="Alias sil">
                  <Trash2 size={11}/>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {data.can_manage ? (
        <div className="flex gap-2 text-xs">
          <input value={newName} onChange={e => setNewName(e.target.value)}
                 placeholder="Yeni alias (ör. E Vitamini, Vitamin E)"
                 className="flex-1 border rounded px-2 py-1"
                 data-testid="rmc-alias-input"/>
          <button onClick={add} disabled={busy || !newName.trim()}
                  className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded px-3 py-1 font-semibold disabled:opacity-50"
                  data-testid="rmc-alias-add-btn">
            + Ekle
          </button>
        </div>
      ) : (
        <div className="text-[10px] text-slate-400 italic">Alias eklemek için Admin veya System Authority yetkisi gereklidir.</div>
      )}
    </div>
  );
}

const empty = {
  trade_name: "", supplier: "", manufacturer: "", subcategory: "",
  inci_entries: [], toxicology_entries: [],
  appearance: "", odor: "", color: "", ph: "", density: "",
  solubility: "", recommended_usage_pct: "", storage_conditions: "",
  technical_notes: "",
  ifra_info: "", allergens: "", usage_restrictions: "", warnings: "",
  regulatory_notes: "", data_source: "",
};

export default function RawMaterialCardModal({ open, materialId, onClose }) {
  const [tab, setTab] = useState("identity");
  const [mat, setMat] = useState(null);
  const [card, setCard] = useState(empty);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    document_type: "SDS", file: null, document_date: "",
    source_url: "", notes: "",
  });
  // iter186 FAZ 2
  const [analyzing, setAnalyzing] = useState(null);        // doc.id being analyzed
  const [suggestion, setSuggestion] = useState(null);      // { doc, extracted_fields, confidence, applied:{key:bool} }
  const [sdsSearchOpen, setSdsSearchOpen] = useState(false);
  const [sdsResults, setSdsResults] = useState(null);
  const [sdsSearching, setSdsSearching] = useState(false);
  const [importingUrl, setImportingUrl] = useState(null);
  // iter191 — allergen extraction
  const [extractingAllergens, setExtractingAllergens] = useState(null);
  const [allergenSuggestion, setAllergenSuggestion] = useState(null); // { doc, allergens:[...], applied:{idx:bool}, verifyAll:false }

  const load = useCallback(async () => {
    if (!materialId) return;
    try {
      const r = await api.get(`/raw-material-cards/${materialId}`);
      setMat(r.material);
      setCard({ ...empty, ...r.card });
      setDocs(r.documents || []);
    } catch (e) { console.error("card load failed:", e); }
  }, [materialId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/raw-material-cards/${materialId}`, card);
      await load();
    } catch (e) { alert("Kaydedilemedi: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const saveToxicology = async () => {
    setBusy(true);
    try {
      await api.put(`/raw-material-cards/${materialId}/toxicology`,
        { entries: card.toxicology_entries || [] });
      await load();
    } catch (e) { alert("Toksikoloji kaydedilemedi: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const uploadDoc = async (opts = {}) => {
    if (!uploadForm.file) { alert("Dosya seçin"); return; }
    setUploading(true);
    try {
      const APIURL = process.env.REACT_APP_BACKEND_URL + "/api";
      const token = localStorage.getItem("erp_token") || sessionStorage.getItem("erp_token") || "";
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      fd.append("document_type", uploadForm.document_type);
      if (uploadForm.document_date) fd.append("document_date", uploadForm.document_date);
      if (uploadForm.source_url) fd.append("source_url", uploadForm.source_url);
      if (uploadForm.notes) fd.append("notes", uploadForm.notes);
      // iter239.1 — Soft confirmation for >30 MB. First attempt without,
      // retry with confirm_large=true after the user accepts the dialog.
      if (opts.confirmLarge) fd.append("confirm_large", "true");
      const r = await fetch(`${APIURL}/raw-material-cards/${materialId}/documents`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (r.status === 409) {
        const d = await r.json().catch(() => ({}));
        const det = d.detail || {};
        if (det.code === "large_document_confirmation_required") {
          if (window.confirm(det.message || "Bu belge 30 MB üzerindedir. Devam etmek istiyor musunuz?")) {
            setUploading(false);
            return uploadDoc({ confirmLarge: true });
          }
          setUploading(false);
          return;  // user cancelled — no upload, no record
        }
      }
      if (r.status === 413) {
        const d = await r.json().catch(() => ({}));
        const det = d.detail || {};
        alert(det.message || "Belge izin verilen maksimum dosya boyutunu aşıyor.");
        setUploading(false);
        return;
      }
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail?.message || d.detail || "Yükleme başarısız");
      }
      setUploadForm({ document_type: "SDS", file: null, document_date: "", source_url: "", notes: "" });
      await load();
    } catch (e) { alert("Hata: " + e.message); }
    finally { setUploading(false); }
  };

  const downloadDoc = async (d) => {
    try {
      const APIURL = process.env.REACT_APP_BACKEND_URL + "/api";
      const token = localStorage.getItem("erp_token") || sessionStorage.getItem("erp_token") || "";
      const r = await fetch(`${APIURL}/raw-material-cards/documents/${d.id}/download`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("İndirilemedi");
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = d.original_filename || `${d.document_type}-v${d.version}`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { alert("Hata: " + e.message); }
  };

  const deleteDoc = async (d) => {
    if (!window.confirm(`${d.document_type} v${d.version} silinsin mi?`)) return;
    try { await api.del(`/raw-material-cards/documents/${d.id}`); load(); }
    catch (e) { alert("Silinemedi"); }
  };

  const openPdf = async () => {
    try {
      const APIURL = process.env.REACT_APP_BACKEND_URL + "/api";
      const token = localStorage.getItem("erp_token") || sessionStorage.getItem("erp_token") || "";
      const r = await fetch(`${APIURL}/raw-material-cards/${materialId}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("PDF alınamadı");
      const blob = await r.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) { alert("Hata: " + e.message); }
  };

  // iter186 FAZ 2 — AI analyze / apply suggestions / SDS discovery
  const analyzeDoc = async (d, force = false) => {
    setAnalyzing(d.id);
    try {
      const r = await api.post(
        `/raw-material-cards/documents/${d.id}/analyze${force ? "?force=true" : ""}`, {});
      if (r.status && r.status !== "ok") {
        alert(`AI analiz başarısız: ${r.error || "bilinmeyen hata"}\nManuel giriş yapabilirsiniz. Belge kartta korunuyor.`);
        await load();
        return;
      }
      setSuggestion({
        doc: d, extracted_fields: r.extracted_fields || {},
        confidence: r.confidence || {}, cached: !!r.cached,
        applied: Object.fromEntries(Object.keys(r.extracted_fields || {})
          .filter(k => (r.extracted_fields || {})[k] != null)
          .map(k => [k, true])),
      });
      await load();
    } catch (e) { alert("Hata: " + (e.response?.data?.detail || e.message)); }
    finally { setAnalyzing(null); }
  };

  // iter191 — Fragrance allergen composition AI extraction (opt-in)
  const extractAllergens = async (d, force = false) => {
    setExtractingAllergens(d.id);
    try {
      const r = await api.post(
        `/raw-material-cards/documents/${d.id}/extract-allergens${force ? "?force=true" : ""}`, {});
      if (r.status && r.status !== "ok") {
        alert(`AI çıkarım başarısız: ${r.error || "bilinmeyen hata"}\nManuel giriş yapabilirsiniz. Belge kartta korunuyor.`);
        await load();
        return;
      }
      setAllergenSuggestion({
        doc: d, allergens: r.allergens || [], summary: r.summary || "",
        cached: !!r.cached,
        applied: Object.fromEntries((r.allergens || []).map((_, i) => [i, true])),
        verifyAll: false,
      });
      await load();
    } catch (e) { alert("Hata: " + (e.response?.data?.detail || e.message)); }
    finally { setExtractingAllergens(null); }
  };

  const applyExtractedAllergens = async () => {
    if (!allergenSuggestion) return;
    const rows = (allergenSuggestion.allergens || [])
      .filter((_, i) => allergenSuggestion.applied[i])
      .map(a => ({ ...a, verified: !!allergenSuggestion.verifyAll }));
    if (!rows.length) { alert("Uygulanacak allergen kaydı seçilmedi"); return; }
    try {
      const r = await api.post(
        `/raw-material-cards/${materialId}/apply-extracted-allergens`,
        { entries: rows, source_document_id: allergenSuggestion.doc.id });
      setAllergenSuggestion(null);
      await load();
      alert(`${r.applied} kayıt uygulandı. Toplam: ${r.total}. ${allergenSuggestion.verifyAll ? "Verified: EVET" : "Verified: HAYIR (kullanıcı manuel doğrulamalı)"}`);
    } catch (e) { alert("Uygulanamadı: " + (e.response?.data?.detail || e.message)); }
  };

  // Map extracted-field names → card-schema field names.
  const FIELD_MAP_TO_CARD = {
    supplier: "supplier", manufacturer: "manufacturer",
    appearance: "appearance", color: "color", odor: "odor",
    ph: "ph", density: "density", solubility: "solubility",
    viscosity: "technical_notes", storage_conditions: "storage_conditions",
    recommended_usage_pct: "recommended_usage_pct",
    hazard_notes: "warnings", listed_allergens: "allergens",
    restrictions: "usage_restrictions",
    ifra_version_or_amendment: "ifra_info",
    category_limits: "ifra_info", max_usage_values: "ifra_info",
  };

  const applySuggestions = async () => {
    if (!suggestion) return;
    const applied = suggestion.applied || {};
    const fields = {};
    for (const [k, on] of Object.entries(applied)) {
      if (!on) continue;
      const v = suggestion.extracted_fields[k];
      if (v == null || v === "") continue;
      if (k === "inci_or_chemical_name" || k === "cas_no" || k === "ec_no") {
        fields.inci_entries = fields.inci_entries || [];
        const existing = fields.inci_entries[0] || {};
        if (k === "inci_or_chemical_name") existing.inci_name = v;
        if (k === "cas_no") existing.cas_no = v;
        if (k === "ec_no") existing.ec_no = v;
        fields.inci_entries[0] = existing;
      } else if (FIELD_MAP_TO_CARD[k]) {
        const cardKey = FIELD_MAP_TO_CARD[k];
        // Concatenate for shared destinations (ifra_info) instead of overwrite
        if (["ifra_info", "warnings", "allergens", "technical_notes"].includes(cardKey) && fields[cardKey]) {
          fields[cardKey] = `${fields[cardKey]}\n${v}`;
        } else {
          fields[cardKey] = v;
        }
      }
    }
    try {
      const r = await api.post(
        `/raw-material-cards/${materialId}/apply-suggestions`
        + `?source_document_id=${suggestion.doc.id}`, { fields });
      alert(`${r.applied} alan uygulandı.`);
      setSuggestion(null);
      await load();
    } catch (e) { alert("Hata: " + (e.response?.data?.detail || e.message)); }
  };

  const searchSds = async () => {
    setSdsSearching(true);
    setSdsResults(null);
    try {
      const inci = (card.inci_entries || [])[0] || {};
      const r = await api.post(`/raw-material-cards/${materialId}/search-sds`, {
        trade_name: card.trade_name, manufacturer: card.manufacturer,
        supplier: card.supplier, cas_no: inci.cas_no,
        inci: inci.inci_name,
      });
      setSdsResults(r);
    } catch (e) { alert("Hata: " + e.message); }
    finally { setSdsSearching(false); }
  };

  const importSds = async (url, opts = {}) => {
    setImportingUrl(url);
    try {
      const r = await api.post(`/raw-material-cards/${materialId}/import-sds`,
        { url, document_type: "SDS",
          ...(opts.confirmLarge ? { confirm_large: true } : {}) });
      if (r.duplicate) {
        alert("Bu belge zaten kayıtlı.");
      } else {
        alert("SDS R2'ye kaydedildi. Analiz için 'Belgeyi Analiz Et' butonuna basın.");
      }
      setSdsSearchOpen(false); setSdsResults(null);
      await load();
    } catch (e) {
      // iter239.1 — surface soft-confirmation for internet SDS imports.
      const det = e.response?.data?.detail;
      const status = e.response?.status;
      if (status === 409 && det?.code === "large_document_confirmation_required") {
        if (window.confirm(det.message || "Bu belge 30 MB üzerindedir. Devam etmek istiyor musunuz?")) {
          setImportingUrl(null);
          return importSds(url, { confirmLarge: true });
        }
        // user cancelled — no download, no record
      } else if (status === 413) {
        alert(det?.message || "Belge izin verilen maksimum dosya boyutunu aşıyor.");
      } else {
        alert("Hata: " + (det?.message || det || e.message));
      }
    }
    finally { setImportingUrl(null); }
  };

  const addInci = () => setCard({
    ...card,
    inci_entries: [...(card.inci_entries || []),
                   { inci_name: "", cas_no: "", ec_no: "", percentage: null, source: "" }],
  });
  const setInci = (i, patch) => {
    const next = [...(card.inci_entries || [])];
    next[i] = { ...next[i], ...patch }; setCard({ ...card, inci_entries: next });
  };
  const rmInci = (i) => {
    const next = [...(card.inci_entries || [])]; next.splice(i, 1);
    setCard({ ...card, inci_entries: next });
  };

  const hasCore = !!(card.trade_name || card.supplier || (card.inci_entries || []).length);
  const hasDocs = docs.some(d => d.is_current);
  const statusColor = hasCore && hasDocs ? "emerald" : "amber";

  return (
    <Modal open={open} onClose={onClose}
           title={`Hammadde Kartı — ${mat?.code || ""} ${mat?.name || ""}`} size="xl">
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-2 text-xs">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ring-1 bg-${statusColor}-50 text-${statusColor}-700 ring-${statusColor}-600/20`}
                data-testid="rmc-status-badge">
            <span className={`inline-block w-1.5 h-1.5 rounded-full bg-${statusColor}-500`}></span>
            {hasCore && hasDocs ? "Bilgiler mevcut" : "Eksik bilgi/belge"}
          </span>
          <span className="text-slate-500">Bilgi eksikliği stok işlemlerini engellemez.</span>
        </div>
        <div className="flex gap-2">
          <Btn variant="secondary" onClick={() => setSdsSearchOpen(true)}
               data-testid="rmc-sds-search-btn">
            <Globe size={12}/> İnternetten SDS Bul
          </Btn>
          <Btn variant="secondary" onClick={openPdf} data-testid="rmc-pdf-btn">
            <FileText size={12}/> PDF Oluştur
          </Btn>
        </div>
      </div>

      <div className="border-b border-slate-200 mb-3 flex gap-3">
        {[["identity", "Kimlik / Mevzuat"], ["tech", "Teknik"],
          ["reg", "Regülasyon"], ["toxicology", "Toksikoloji"],
          ["docs", `Belgeler (${docs.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
                  data-testid={`rmc-tab-${k}`}
                  className={`px-1 py-2 text-sm border-b-2 ${tab === k ? "border-indigo-600 text-indigo-700 font-medium" : "border-transparent text-slate-500"}`}>{l}</button>
        ))}
      </div>

      {tab === "identity" && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Ticari Ad" value={card.trade_name || ""}
                   onChange={e => setCard({ ...card, trade_name: e.target.value })}
                   data-testid="rmc-trade-name"/>
            <Input label="Alt Kategori" value={card.subcategory || ""}
                   onChange={e => setCard({ ...card, subcategory: e.target.value })}
                   data-testid="rmc-subcategory"/>
            <Input label="Tedarikçi" value={card.supplier || ""}
                   onChange={e => setCard({ ...card, supplier: e.target.value })}
                   data-testid="rmc-supplier"/>
            <Input label="Üretici" value={card.manufacturer || ""}
                   onChange={e => setCard({ ...card, manufacturer: e.target.value })}
                   data-testid="rmc-manufacturer"/>
          </div>
          <TradeNamesSection materialId={materialId} card={card} onChanged={load}/>
          <DocAliasesSection materialId={materialId} onChanged={load}/>
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm font-semibold">INCI / CAS Bileşenleri</div>
              <Btn size="sm" variant="secondary" onClick={addInci} data-testid="rmc-add-inci">
                <Plus size={12}/> Bileşen Ekle
              </Btn>
            </div>
            <div className="space-y-1">
              {(card.inci_entries || []).map((e, i) => (
                <div key={i} className="grid grid-cols-12 gap-1 items-center border border-slate-200 rounded p-2">
                  <input className="col-span-4 px-2 py-1 text-xs border rounded"
                         placeholder="INCI Name" value={e.inci_name || ""}
                         onChange={ev => setInci(i, { inci_name: ev.target.value })}
                         data-testid={`rmc-inci-name-${i}`}/>
                  <input className="col-span-2 px-2 py-1 text-xs border rounded"
                         placeholder="CAS No" value={e.cas_no || ""}
                         onChange={ev => setInci(i, { cas_no: ev.target.value })}/>
                  <input className="col-span-2 px-2 py-1 text-xs border rounded"
                         placeholder="EC No" value={e.ec_no || ""}
                         onChange={ev => setInci(i, { ec_no: ev.target.value })}/>
                  <input type="number" step="0.01"
                         className="col-span-1 px-2 py-1 text-xs border rounded font-mono"
                         placeholder="%" value={e.percentage ?? ""}
                         onChange={ev => setInci(i, { percentage: parseFloat(ev.target.value) || null })}/>
                  <input className="col-span-2 px-2 py-1 text-xs border rounded"
                         placeholder="Kaynak" value={e.source || ""}
                         onChange={ev => setInci(i, { source: ev.target.value })}/>
                  <button onClick={() => rmInci(i)} className="col-span-1 text-rose-600 hover:text-rose-800"
                          data-testid={`rmc-rm-inci-${i}`}>
                    <X size={14}/>
                  </button>
                </div>
              ))}
              {!(card.inci_entries || []).length && (
                <div className="text-xs text-slate-400 py-2">Kayıtlı INCI/CAS yok.</div>
              )}
            </div>
          </div>
          <Input label="Veri Kaynağı" value={card.data_source || ""}
                 onChange={e => setCard({ ...card, data_source: e.target.value })}
                 placeholder="Örn: Supplier SDS V2 · 2026-01"
                 data-testid="rmc-data-source"/>
        </div>
      )}

      {tab === "tech" && (
        <div className="grid grid-cols-2 gap-3">
          <Input label="Görünüm" value={card.appearance || ""}
                 onChange={e => setCard({ ...card, appearance: e.target.value })}/>
          <Input label="Koku" value={card.odor || ""}
                 onChange={e => setCard({ ...card, odor: e.target.value })}/>
          <Input label="Renk" value={card.color || ""}
                 onChange={e => setCard({ ...card, color: e.target.value })}/>
          <Input label="pH" value={card.ph || ""}
                 onChange={e => setCard({ ...card, ph: e.target.value })}/>
          <Input label="Yoğunluk" value={card.density || ""}
                 onChange={e => setCard({ ...card, density: e.target.value })}/>
          <Input label="Çözünürlük" value={card.solubility || ""}
                 onChange={e => setCard({ ...card, solubility: e.target.value })}/>
          <Input label="Önerilen Kullanım %" value={card.recommended_usage_pct || ""}
                 onChange={e => setCard({ ...card, recommended_usage_pct: e.target.value })}/>
          <Input label="Saklama Koşulları" value={card.storage_conditions || ""}
                 onChange={e => setCard({ ...card, storage_conditions: e.target.value })}/>
          <div className="col-span-2">
            <Textarea label="Teknik Not" rows={3} value={card.technical_notes || ""}
                      onChange={e => setCard({ ...card, technical_notes: e.target.value })}/>
          </div>
        </div>
      )}

      {tab === "reg" && (
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Textarea label="IFRA" rows={2} value={card.ifra_info || ""}
                      onChange={e => setCard({ ...card, ifra_info: e.target.value })}/>
          </div>
          <Textarea label="Alerjenler" rows={3} value={card.allergens || ""}
                    onChange={e => setCard({ ...card, allergens: e.target.value })}/>
          <Textarea label="Kullanım Kısıtlamaları" rows={3} value={card.usage_restrictions || ""}
                    onChange={e => setCard({ ...card, usage_restrictions: e.target.value })}/>
          <Textarea label="Uyarılar" rows={3} value={card.warnings || ""}
                    onChange={e => setCard({ ...card, warnings: e.target.value })}/>
          <Textarea label="Regülasyon Notu" rows={3} value={card.regulatory_notes || ""}
                    onChange={e => setCard({ ...card, regulatory_notes: e.target.value })}/>
        </div>
      )}

      {tab === "toxicology" && (
        <div className="space-y-3" data-testid="rmc-toxicology-pane">
          <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
            iter189 — Bu alandaki değerler <b>Ürün Güvenlik Ön Değerlendirmesi</b> (TASLAK) için
            kullanılır. <b>Uydurma yok</b>: sadece <b>Doğrulanmış (verified)</b> olarak işaretlenmiş
            kayıtlar SED/MoS hesabına dahil edilir. Bu çıktı resmi CPSR değildir.
          </div>
          <div className="flex justify-between items-center">
            <div className="text-sm font-semibold">Toksikoloji Kayıtları</div>
            <Btn size="sm" variant="secondary" data-testid="rmc-tox-add"
                 onClick={() => setCard({ ...card, toxicology_entries: [
                   ...(card.toxicology_entries || []),
                   { inci_name: "", cas_number: "", noael_value: null,
                     noael_unit: "mg/kg bw/day", noael_route: "oral",
                     noael_species: "", noael_study_type: "",
                     dermal_absorption_percent: null, verified: false,
                     source_type: "", source_title: "", source_url: "",
                     source_organization: "", source_version: "",
                     effective_date: "", toxicology_notes: "" }] })}>
              <Plus size={12}/> Kayıt Ekle
            </Btn>
          </div>
          {!(card.toxicology_entries || []).length && (
            <div className="text-xs text-slate-400 py-2">Kayıtlı toksikoloji verisi yok.</div>
          )}
          <div className="space-y-3">
            {(card.toxicology_entries || []).map((t, i) => {
              const upd = (patch) => {
                const nx = [...(card.toxicology_entries || [])];
                nx[i] = { ...nx[i], ...patch };
                setCard({ ...card, toxicology_entries: nx });
              };
              const rm = () => {
                const nx = [...(card.toxicology_entries || [])];
                nx.splice(i, 1);
                setCard({ ...card, toxicology_entries: nx });
              };
              return (
                <div key={i} className={`border rounded p-2 space-y-2 ${t.verified ? "border-emerald-400 bg-emerald-50/30" : "border-slate-200"}`}
                     data-testid={`rmc-tox-row-${i}`}>
                  <div className="grid grid-cols-6 gap-1">
                    <input className="col-span-2 px-2 py-1 text-xs border rounded"
                           placeholder="INCI Name" value={t.inci_name || ""}
                           onChange={e => upd({ inci_name: e.target.value })}
                           data-testid={`rmc-tox-inci-${i}`}/>
                    <input className="col-span-1 px-2 py-1 text-xs border rounded"
                           placeholder="CAS" value={t.cas_number || ""}
                           onChange={e => upd({ cas_number: e.target.value })}/>
                    <input type="number" step="0.0001"
                           className="col-span-1 px-2 py-1 text-xs border rounded font-mono"
                           placeholder="NOAEL" value={t.noael_value ?? ""}
                           onChange={e => upd({ noael_value: e.target.value === "" ? null : parseFloat(e.target.value) })}
                           data-testid={`rmc-tox-noael-${i}`}/>
                    <input className="col-span-1 px-2 py-1 text-xs border rounded"
                           placeholder="NOAEL Unit" value={t.noael_unit || ""}
                           onChange={e => upd({ noael_unit: e.target.value })}/>
                    <button onClick={rm} className="col-span-1 text-rose-600 hover:text-rose-800 text-xs"
                            data-testid={`rmc-tox-rm-${i}`}><X size={14}/></button>
                  </div>
                  <div className="grid grid-cols-6 gap-1">
                    <select className="col-span-1 px-2 py-1 text-xs border rounded"
                            value={t.noael_route || ""}
                            onChange={e => upd({ noael_route: e.target.value })}>
                      <option value="">Rota</option>
                      <option value="oral">oral</option>
                      <option value="dermal">dermal</option>
                      <option value="inhalation">inhalation</option>
                    </select>
                    <input className="col-span-1 px-2 py-1 text-xs border rounded"
                           placeholder="Türü (rat/rabbit)" value={t.noael_species || ""}
                           onChange={e => upd({ noael_species: e.target.value })}/>
                    <input className="col-span-2 px-2 py-1 text-xs border rounded"
                           placeholder="Çalışma tipi" value={t.noael_study_type || ""}
                           onChange={e => upd({ noael_study_type: e.target.value })}/>
                    <input type="number" step="0.1"
                           className="col-span-1 px-2 py-1 text-xs border rounded font-mono"
                           placeholder="DAp %" value={t.dermal_absorption_percent ?? ""}
                           onChange={e => upd({ dermal_absorption_percent: e.target.value === "" ? null : parseFloat(e.target.value) })}
                           data-testid={`rmc-tox-dap-${i}`}/>
                    <label className="col-span-1 flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={!!t.verified}
                             onChange={e => upd({ verified: e.target.checked })}
                             data-testid={`rmc-tox-verified-${i}`}/>
                      <span className={t.verified ? "text-emerald-700 font-semibold" : "text-slate-500"}>Doğrulandı</span>
                    </label>
                  </div>
                  <div className="grid grid-cols-6 gap-1">
                    <input className="col-span-2 px-2 py-1 text-xs border rounded"
                           placeholder="Kaynak (SCCS/1234 vb)" value={t.source_title || ""}
                           onChange={e => upd({ source_title: e.target.value })}/>
                    <input className="col-span-2 px-2 py-1 text-xs border rounded"
                           placeholder="Kaynak URL" value={t.source_url || ""}
                           onChange={e => upd({ source_url: e.target.value })}/>
                    <input className="col-span-1 px-2 py-1 text-xs border rounded"
                           placeholder="Organizasyon" value={t.source_organization || ""}
                           onChange={e => upd({ source_organization: e.target.value })}/>
                    <input type="date" className="col-span-1 px-2 py-1 text-xs border rounded"
                           value={t.effective_date || ""}
                           onChange={e => upd({ effective_date: e.target.value })}/>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end">
            <Btn onClick={saveToxicology} disabled={busy} data-testid="rmc-tox-save">
              {busy ? "Kaydediliyor..." : "Toksikolojiyi Kaydet"}
            </Btn>
          </div>
        </div>
      )}

      {tab === "docs" && (
        <div className="space-y-3">
          <Card className="p-3 bg-sky-50/40 border-sky-200">
            <div className="text-xs font-semibold text-sky-900 mb-2">Yeni Belge Yükle</div>
            <div className="grid grid-cols-2 gap-2">
              <Select label="Belge Tipi" value={uploadForm.document_type}
                      onChange={e => setUploadForm({ ...uploadForm, document_type: e.target.value })}
                      data-testid="rmc-upload-type">
                {DOC_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </Select>
              <Input label="Belge Tarihi" type="date" value={uploadForm.document_date}
                     onChange={e => setUploadForm({ ...uploadForm, document_date: e.target.value })}/>
              <div className="col-span-2">
                <input type="file" onChange={e => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                       data-testid="rmc-upload-file" className="text-xs w-full"/>
              </div>
              <Input label="Kaynak URL" value={uploadForm.source_url}
                     onChange={e => setUploadForm({ ...uploadForm, source_url: e.target.value })}/>
              <Input label="Not" value={uploadForm.notes}
                     onChange={e => setUploadForm({ ...uploadForm, notes: e.target.value })}/>
            </div>
            <div className="mt-2">
              <Btn onClick={uploadDoc} disabled={uploading || !uploadForm.file}
                   data-testid="rmc-upload-submit">
                <Upload size={12}/> {uploading ? "Yükleniyor..." : "R2'ye Yükle"}
              </Btn>
            </div>
          </Card>

          <div className="border border-slate-200 rounded bg-white">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="p-1.5 text-left">Tip</th>
                  <th className="p-1.5 text-center">Ver</th>
                  <th className="p-1.5 text-center">Aktif</th>
                  <th className="p-1.5 text-left">Tarih</th>
                  <th className="p-1.5 text-left">Dosya</th>
                  <th className="p-1.5 text-right">Boyut</th>
                  <th className="p-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {docs.map(d => (
                  <tr key={d.id} className="border-t" data-testid={`rmc-doc-row-${d.id}`}>
                    <td className="p-1.5 font-mono">{d.document_type}</td>
                    <td className="p-1.5 text-center">v{d.version}</td>
                    <td className="p-1.5 text-center">
                      {d.is_current ? <span className="text-emerald-600">✓</span> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="p-1.5">{(d.document_date || d.created_at || "").slice(0, 10)}</td>
                    <td className="p-1.5 truncate max-w-[200px]" title={d.original_filename}>{d.original_filename}</td>
                    <td className="p-1.5 text-right font-mono">{Math.max(1, Math.round((d.size || 0) / 1024))} KB</td>
                    <td className="p-1.5 text-right">
                      <div className="flex gap-1 justify-end">
                        <Btn size="sm" variant="secondary" onClick={() => analyzeDoc(d)}
                             disabled={analyzing === d.id}
                             data-testid={`rmc-doc-analyze-${d.id}`}
                             title={d.analysis_status === "ok" ? "Yeniden Analiz Et" : "Belgeyi Analiz Et"}>
                          {analyzing === d.id ? <RefreshCw size={11} className="animate-spin"/> : <Sparkles size={11}/>}
                          {d.analysis_status === "ok" ? (
                            <span className="text-emerald-700 text-[10px] ml-0.5">Analiz ✓</span>
                          ) : d.analysis_status === "failed" ? (
                            <span className="text-rose-700 text-[10px] ml-0.5">Hatalı</span>
                          ) : (
                            <span className="text-[10px] ml-0.5">Analiz</span>
                          )}
                        </Btn>
                        <Btn size="sm" variant="secondary" onClick={() => downloadDoc(d)}
                             data-testid={`rmc-doc-dl-${d.id}`}><Download size={11}/></Btn>
                        {(d.document_type === "IFRA" || d.document_type === "ALLERGEN" || d.document_type === "SDS") && (
                          <Btn size="sm" variant="secondary" onClick={() => extractAllergens(d)}
                               disabled={extractingAllergens === d.id}
                               data-testid={`rmc-doc-extract-allergens-${d.id}`}
                               title="Alerjenleri AI ile çıkar (opt-in)">
                            {extractingAllergens === d.id ? <RefreshCw size={11} className="animate-spin"/> : <Sparkles size={11}/>}
                            <span className="text-[10px] ml-0.5">
                              {d.allergen_extraction_status === "ok" ? "Alerjenler ✓" : "Alerjen Çıkar"}
                            </span>
                          </Btn>
                        )}
                        <Btn size="sm" variant="danger" onClick={() => deleteDoc(d)}
                             data-testid={`rmc-doc-del-${d.id}`}><Trash2 size={11}/></Btn>
                      </div>
                    </td>
                  </tr>
                ))}
                {!docs.length && (
                  <tr><td colSpan={7} className="p-4 text-center text-slate-400">Belge yok.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
        <Btn variant="secondary" onClick={onClose}>Kapat</Btn>
        {tab !== "docs" && tab !== "toxicology" && (
          <Btn onClick={save} disabled={busy} data-testid="rmc-save-btn">
            {busy ? "Kaydediliyor..." : "Kaydet"}
          </Btn>
        )}
      </div>

      {/* iter186 — AI suggestion review modal (embedded) */}
      {suggestion && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
             onClick={() => setSuggestion(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()} data-testid="rmc-suggestion-modal">
            <div className="p-3 border-b flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm">
                  <Sparkles size={14} className="inline text-indigo-600"/> Belge Analizi Önerileri
                </div>
                <div className="text-[11px] text-slate-500">
                  {suggestion.doc.document_type} v{suggestion.doc.version} · {suggestion.doc.original_filename}
                  {suggestion.cached && <span className="ml-2 text-amber-600">· Önbellek</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <Btn size="sm" variant="secondary" onClick={() => analyzeDoc(suggestion.doc, true)}
                     data-testid="rmc-suggestion-reanalyze">Yeniden Analiz</Btn>
                <Btn size="sm" variant="secondary" onClick={() => {
                  const next = { ...suggestion.applied };
                  const anyOn = Object.values(next).some(x => x);
                  Object.keys(suggestion.extracted_fields).forEach(k => {
                    next[k] = !anyOn && suggestion.extracted_fields[k] != null;
                  });
                  setSuggestion({ ...suggestion, applied: next });
                }} data-testid="rmc-suggestion-select-all">Tümünü Seç/Bırak</Btn>
              </div>
            </div>
            <div className="p-3 overflow-y-auto flex-1">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-600">
                  <tr>
                    <th className="p-1.5 text-left">Alan</th>
                    <th className="p-1.5 text-left">Belgeden Bulunan</th>
                    <th className="p-1.5 text-center">Güven</th>
                    <th className="p-1.5 text-center">Uygula</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(suggestion.extracted_fields).map(([k, v]) => {
                    const conf = suggestion.confidence[k] || "low";
                    const disabled = v == null || v === "";
                    return (
                      <tr key={k} className="border-t" data-testid={`rmc-sug-row-${k}`}>
                        <td className="p-1.5 font-mono text-slate-700">{k}</td>
                        <td className="p-1.5">{disabled ? <span className="text-slate-400">—</span> : String(v).slice(0, 200)}</td>
                        <td className="p-1.5 text-center">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${conf === "high" ? "bg-emerald-100 text-emerald-700" : conf === "medium" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{conf}</span>
                        </td>
                        <td className="p-1.5 text-center">
                          <input type="checkbox" checked={!!suggestion.applied[k]} disabled={disabled}
                                 onChange={e => setSuggestion({
                                   ...suggestion,
                                   applied: { ...suggestion.applied, [k]: e.target.checked },
                                 })}
                                 data-testid={`rmc-sug-check-${k}`}/>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setSuggestion(null)}>Kapat</Btn>
              <Btn onClick={applySuggestions} data-testid="rmc-suggestion-apply">
                <Check size={12}/> Seçili Alanları Uygula
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* iter186 — Internet SDS discovery modal */}
      {sdsSearchOpen && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
             onClick={() => { if (!importingUrl) setSdsSearchOpen(false); }}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()} data-testid="rmc-sds-modal">
            <div className="p-3 border-b flex items-center justify-between">
              <div className="font-semibold text-sm">
                <Globe size={14} className="inline text-sky-700"/> İnternetten SDS Bul
              </div>
              <Btn size="sm" onClick={searchSds} disabled={sdsSearching}
                   data-testid="rmc-sds-run">
                {sdsSearching ? <RefreshCw size={11} className="animate-spin"/> : <Globe size={11}/>}
                {sdsSearching ? "Aranıyor..." : "Arat"}
              </Btn>
            </div>
            <div className="p-3 overflow-y-auto flex-1">
              {!sdsResults && !sdsSearching && (
                <div className="text-xs text-slate-500">
                  "Arat" butonuna basın. Sorgu için Hammadde Kartındaki
                  ticari ad, üretici, tedarikçi, CAS ve INCI kullanılacak.
                </div>
              )}
              {sdsResults && sdsResults.results && sdsResults.results.length === 0 && (
                <div className="text-xs text-slate-500">Sonuç bulunamadı.</div>
              )}
              {sdsResults && (sdsResults.results || []).map((r, i) => (
                <div key={i} className="border rounded p-2 mb-2 text-xs" data-testid={`rmc-sds-result-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className={`text-[10px] font-semibold uppercase inline-block px-1.5 py-0.5 rounded ${r.tier === "high" ? "bg-emerald-100 text-emerald-700" : r.tier === "medium" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                        {r.tier === "high" ? "🟢 Yüksek" : r.tier === "medium" ? "🟡 Muhtemel" : "🔴 Şüpheli"}
                      </div>
                      <div className="font-semibold mt-1 truncate">{r.title}</div>
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-sky-700 text-[11px] break-all">{r.url}</a>
                      <div className="text-slate-600 mt-1 line-clamp-2">{r.snippet}</div>
                      <div className="text-[10px] text-slate-400 mt-1">domain: {r.domain} · score: {r.score}</div>
                    </div>
                    <Btn size="sm" onClick={() => importSds(r.url)}
                         disabled={!!importingUrl}
                         data-testid={`rmc-sds-import-${i}`}>
                      {importingUrl === r.url ? <RefreshCw size={11} className="animate-spin"/> : <Download size={11}/>}
                      Bu SDS'yi Kullan
                    </Btn>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setSdsSearchOpen(false)}
                   disabled={!!importingUrl}>Kapat</Btn>
            </div>
          </div>
        </div>
      )}

      {/* iter191 — Fragrance Allergen AI Extraction review modal */}
      {allergenSuggestion && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
             onClick={() => setAllergenSuggestion(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()} data-testid="rmc-allergen-suggestion-modal">
            <div className="p-3 border-b">
              <div className="font-semibold text-sm">
                <Sparkles size={14} className="inline text-indigo-600"/> Alerjen Çıkarımı — İnceleme
              </div>
              <div className="text-[11px] text-slate-500">
                {allergenSuggestion.doc.document_type} v{allergenSuggestion.doc.version} · {allergenSuggestion.doc.original_filename}
                {allergenSuggestion.cached && <span className="ml-2 text-amber-600">· Önbellek</span>}
              </div>
              {allergenSuggestion.summary && (
                <div className="text-[11px] text-slate-600 mt-1 italic">{allergenSuggestion.summary}</div>
              )}
            </div>
            <div className="p-3 overflow-y-auto flex-1">
              {(allergenSuggestion.allergens || []).length === 0 ? (
                <div className="text-xs text-slate-500">Alerjen bulunamadı. Belge içeriğini gözden geçirin.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="p-1.5"></th>
                      <th className="p-1.5 text-left">INCI</th>
                      <th className="p-1.5 text-left">CAS</th>
                      <th className="p-1.5 text-right">% (esansta)</th>
                      <th className="p-1.5 text-left">Not</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allergenSuggestion.allergens.map((a, i) => (
                      <tr key={i} className="border-t" data-testid={`rmc-allergen-row-${i}`}>
                        <td className="p-1.5">
                          <input type="checkbox" checked={!!allergenSuggestion.applied[i]}
                                 onChange={e => setAllergenSuggestion({
                                   ...allergenSuggestion,
                                   applied: { ...allergenSuggestion.applied, [i]: e.target.checked }
                                 })}
                                 data-testid={`rmc-allergen-check-${i}`}/>
                        </td>
                        <td className="p-1.5 font-mono">{a.inci_name}</td>
                        <td className="p-1.5">{a.cas_number || "—"}</td>
                        <td className="p-1.5 text-right font-mono">
                          {a.percentage != null ? `%${a.percentage}` : "—"}
                        </td>
                        <td className="p-1.5 text-slate-600 text-[10px]">{a.note || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-[11px]">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={allergenSuggestion.verifyAll}
                         onChange={e => setAllergenSuggestion({ ...allergenSuggestion, verifyAll: e.target.checked })}
                         data-testid="rmc-allergen-verify-all"/>
                  <span><b>Doğrulandı</b> olarak işaretle (kaynak belgeyi inceledim, veriler doğru).</span>
                </label>
                <div className="text-slate-500 mt-1">
                  Doğrulanmadan uygulanan kayıtlar mevzuat kontrolünde YELLOW üretir.
                  Kaynak: <span className="font-mono">{allergenSuggestion.doc.original_filename}</span>
                </div>
              </div>
            </div>
            <div className="p-3 border-t flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setAllergenSuggestion(null)}>Vazgeç</Btn>
              <Btn onClick={applyExtractedAllergens} data-testid="rmc-allergen-apply-btn">
                <CheckCircle size={12}/> Seçili Alerjenleri Uygula
              </Btn>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
